import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import WithdrawModal from '../WithdrawModal/WithdrawModal';
import ThemeToggle from '../../../../components/ThemeToggle/ThemeToggle';
import { Bell, User, Lock, LogOut, ChevronDown } from 'lucide-react';
import api from '../../../../utils/api';
import './DashboardNavbar.css';

const DashboardNavbar = ({ onMenuClick, isOpen }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isAdminPath = location.pathname.startsWith('/dashboard/admin');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (id = null) => {
    try {
      await api.post('/notifications/read', { id });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNav = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const initials = (user?.username || 'U').slice(0, 2).toUpperCase();

  return (
    <>
      <nav className={`dnav ${isAdminPath ? 'dnav--admin' : ''}`}>
        {/* Left: hamburger */}
        <div className="dnav__left">
          <button 
            type="button" 
            className={`dnav__hamburger ${isOpen ? 'dnav__hamburger--open' : ''}`} 
            onClick={onMenuClick}
            aria-label="Toggle Menu"
          >
            <span /><span /><span />
          </button>

          {isAdminPath && (
            <div className="dnav__admin-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Admin
            </div>
          )}
        </div>

        {/* Right section: action buttons + profile */}
        <div className="dnav__right">

          <ThemeToggle compact className="dnav__theme-toggle" />

          {/* Notifications */}
          <div className="dnav__notif-wrapper" ref={notifRef}>
            <button 
              className={`dnav__notif-btn ${notifOpen ? 'dnav__notif-btn--active' : ''}`}
              onClick={() => setNotifOpen(!notifOpen)}
            >
              <Bell size={20} />
              {notifications.some(n => !n.isRead) && (
                <span className="dnav__notif-badge" />
              )}
            </button>

            {notifOpen && (
              <div className="dnav__notif-dropdown">
                <div className="dnav__notif-header">
                  <h3>Notifications</h3>
                  <button onClick={() => markAsRead()}>Mark all as read</button>
                </div>
                <div className="dnav__notif-list">
                  {notifications.length === 0 ? (
                    <div className="dnav__notif-empty">No new notifications</div>
                  ) : (
                    notifications.map(n => (
                      <div 
                        key={n.id} 
                        className={`dnav__notif-item ${!n.isRead ? 'dnav__notif-item--unread' : ''}`}
                        onClick={() => markAsRead(n.id)}
                      >
                        <div className="dnav__notif-msg">{n.message}</div>
                        <div className="dnav__notif-time">{n.createdAt}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="dnav__profile-wrapper" ref={dropdownRef}>
            <button className="dnav__profile-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="dnav__avatar">{initials}</div>
              <div className="dnav__user-info">
                <span className="dnav__username">{user?.username || 'Guest'}</span>
                <span className="dnav__role">{isAdminPath ? 'Admin' : (user?.role || 'user')}</span>
              </div>
              <ChevronDown className={`dnav__chevron ${dropdownOpen ? 'dnav__chevron--open' : ''}`} size={16} />
            </button>

            {dropdownOpen && (
              <div className="dnav__dropdown">
                <div className="dnav__dropdown-header">
                  <div className="dnav__dropdown-avatar">{initials}</div>
                  <div>
                    <div className="dnav__dropdown-name">{user?.username}</div>
                    <div className="dnav__dropdown-email">{user?.email || 'user@getinfinite.site'}</div>
                  </div>
                </div>
                <div className="dnav__dropdown-divider" />
                <button className="dnav__dropdown-item" onClick={() => handleNav('/dashboard/profile')}>
                  <User size={16} />
                  My Profile
                </button>
                <button className="dnav__dropdown-item" onClick={() => handleNav('/dashboard/change-password')}>
                  <Lock size={16} />
                  Change Password
                </button>
                <div className="dnav__dropdown-divider" />
                <button className="dnav__dropdown-item dnav__dropdown-item--danger" onClick={handleLogout}>
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <WithdrawModal isOpen={withdrawOpen} onClose={() => setWithdrawOpen(false)} />
    </>
  );
};

export default DashboardNavbar;